// Timezone helpers. Port of app/core/functions/timezones.php.
//
// Read migration-plan.md §6 before changing anything here. The short version:
//
//   INSTANTS (created_at, sent_at, closed_at, *_notified_at, chat stamps) are
//   stored in UTC and rendered in the VIEWER's zone.
//
//   EVENT SCHEDULES (event_date + start_time/end_time/end_date, feed_post_time)
//   are stored as NAIVE wall-clock values meaning "local time in the event's
//   AUTHORING zone", and rendered converted to the viewer's zone. They are
//   deliberately NOT UTC: a fixed-UTC recurring "7 PM" drifts an hour across a
//   DST boundary. Naive-local plus an IANA id is DST-safe and matches how
//   iCalendar does it (every VEVENT carries its own TZID).
//
// Two zone resolutions exist and must never be conflated:
//
//   AUTHORING zone — what a stored naive time MEANS.
//     event.time_zone -> owning group's time_zone -> site zone.
//     Per-EVENT, not per-group: a group can span zones or hold events in other
//     cities, and snapshotting the zone onto the event means a later change to
//     the group's zone never shifts events that already exist.
//
//   DISPLAY zone — what zone to show it IN.
//     viewer's time_zone -> site zone. The group is NOT a display fallback;
//     an anonymous or unset viewer sees the site zone.
//
// Every function here is pure. Zones are passed in explicitly rather than read
// from ambient state, because there is no request-global to read on the server
// and no session on the client. `server/src/time.ts` binds the configured site
// zone so callers do not repeat it.
import { DateTime } from 'luxon';
/** Used only when no site zone is configured. Config should always supply one. */
export const SITE_TZ_FALLBACK = 'America/Los_Angeles';
/** Named formats, so the server and the SPA render a value identically. */
export const TimeFormat = {
    /** "Thu, Jul 16 at 7:00 PM PDT" */
    EVENT_DATETIME: "EEE, MMM d 'at' h:mm a ZZZZ",
    /** "7:00 PM PDT" */
    EVENT_TIME: 'h:mm a ZZZZ',
    /** "Thu, Jul 16" */
    EVENT_DATE: 'EEE, MMM d',
    /** "Jul 16, 2026 7:00 PM PDT" */
    INSTANT: 'MMM d, yyyy h:mm a ZZZZ',
    /** "Jul 16" — the absolute fallback for a relative timestamp. */
    SHORT_DATE: 'MMM d',
    /** Date only, for SQL parameters and date inputs. */
    SQL_DATE: 'yyyy-MM-dd',
    /** Datetime, for SQL parameters. */
    SQL_DATETIME: 'yyyy-MM-dd HH:mm:ss',
};
// --- Validation -----------------------------------------------------------
let canonicalZones = null;
/**
 * The canonical IANA zone set, equivalent to PHP's timezone_identifiers_list().
 *
 * Deliberately stricter than "does this runtime accept it". Node's ICU also
 * resolves legacy aliases like `PST` and `EST`, which are a trap here: they
 * carry no real DST rules, so storing one as an event's authoring zone would
 * quietly reintroduce exactly the drift the naive-local model exists to
 * prevent. Only canonical `Region/City` ids (plus UTC) are accepted.
 */
function zoneSet() {
    if (canonicalZones)
        return canonicalZones;
    const supported = Intl
        .supportedValuesOf;
    const list = typeof supported === 'function' ? supported('timeZone') : [];
    canonicalZones = new Set([...list, 'UTC']);
    return canonicalZones;
}
/** True if `tz` is a canonical IANA identifier. */
export function isValidTz(tz) {
    if (!tz)
        return false;
    return zoneSet().has(tz);
}
/** Port of validTz(): the id if it is valid, otherwise null. */
export function validTz(tz) {
    if (tz === null || tz === undefined || tz === '')
        return null;
    return isValidTz(tz) ? tz : null;
}
/** Every canonical IANA zone, sorted. Drives the timezone picker. */
export function allTimezones() {
    return [...zoneSet()].sort();
}
// --- Zone resolution ------------------------------------------------------
/**
 * DISPLAY zone. Port of viewerTz(): the viewer's saved zone, else the site
 * zone. Note the group is deliberately absent from this chain.
 */
export function resolveViewerTz(viewerZone, siteZone) {
    return validTz(viewerZone) ?? siteZone;
}
/** Port of groupTz() given an already-loaded zone value. */
export function resolveGroupTz(groupZone, siteZone) {
    return validTz(groupZone) ?? siteZone;
}
/**
 * AUTHORING zone. Port of eventTz(): event -> group -> site.
 * An invalid or blank zone at any level falls through to the next one.
 */
export function resolveEventTz(input) {
    return (validTz(input.eventZone) ?? validTz(input.groupZone) ?? input.siteZone);
}
// --- Event schedules (naive wall-clock in an authoring zone) ---------------
/**
 * Port of eventInstant(). Resolve a stored naive date + time to the real moment
 * it refers to, by interpreting it in the authoring zone.
 *
 * `date` is 'yyyy-MM-dd'. `time` is 'HH:mm:ss' or 'HH:mm'; null means midnight
 * (an all-day event). Returns null if the date cannot be parsed.
 */
export function eventInstant(date, time, authoringZone) {
    if (!date)
        return null;
    const t = (time && time.length > 0 ? time : '00:00:00').slice(0, 8);
    const normalized = t.length === 5 ? `${t}:00` : t;
    const dt = DateTime.fromFormat(`${date} ${normalized}`, 'yyyy-MM-dd HH:mm:ss', {
        zone: authoringZone,
    });
    return dt.isValid ? dt : null;
}
/**
 * Port of eventViewerDT(). The event's instant expressed in the viewer's zone,
 * so a caller can format the date part and the time part from ONE moment. Doing
 * them separately is how a card ends up showing the wrong day next to the right
 * time across a midnight boundary.
 */
export function eventViewerDT(date, time, authoringZone, viewerZone) {
    const dt = eventInstant(date, time, authoringZone);
    return dt ? dt.setZone(viewerZone) : null;
}
/**
 * The DISPLAY rule for an event's schedule. Use this, not fmtEventDateTime, for
 * anything a person reads.
 *
 * A timed event converts: 7 PM in New York really is 4 PM for a Los Angeles
 * viewer, and showing the converted moment with its zone suffix is the point.
 *
 * An ALL-DAY event does NOT convert. It has no time to convert, and pretending
 * it starts at midnight then shifting that instant moves the DAY: midnight on
 * the 11th in New York is 9 PM on the 10th in Los Angeles, so an all-day event
 * on the 11th renders as "the 10th" for most of the country. An all-day event is
 * the same calendar day everywhere — that is what all-day means.
 *
 * `dayKey` returns the same decision as a 'yyyy-MM-dd' string, for bucketing an
 * event into a calendar cell. The two must stay in agreement or a card will show
 * one date and sit in another day's cell.
 */
export function fmtEventSchedule(date, time, authoringZone, viewerZone) {
    if (!date)
        return '';
    if (!time)
        return fmtNaiveDate(date, TimeFormat.EVENT_DATE);
    return fmtEventDateTime(date, time, authoringZone, viewerZone, TimeFormat.EVENT_DATETIME);
}
/** The 'yyyy-MM-dd' an event displays on, under the same rule as fmtEventSchedule. */
export function eventDayKey(date, time, authoringZone, viewerZone) {
    if (!date)
        return '';
    if (!time)
        return date;
    const dt = eventViewerDT(date, time, authoringZone, viewerZone);
    return dt ? dt.toFormat(TimeFormat.SQL_DATE) : '';
}
/** Format a bare 'yyyy-MM-dd' with no zone involved. */
export function fmtNaiveDate(date, fmt = TimeFormat.EVENT_DATE) {
    const dt = DateTime.fromFormat(date, 'yyyy-MM-dd', { zone: 'utc' });
    return dt.isValid ? dt.toFormat(fmt) : '';
}
/** Port of fmtEventDateTime(). Empty string when the date cannot be parsed. */
export function fmtEventDateTime(date, time, authoringZone, viewerZone, fmt = TimeFormat.EVENT_DATETIME) {
    const dt = eventViewerDT(date, time, authoringZone, viewerZone);
    return dt ? dt.toFormat(fmt) : '';
}
// --- Instants (stored UTC) ------------------------------------------------
/**
 * Parse a DB datetime string that is known to be UTC. Accepts both the MySQL
 * 'yyyy-MM-dd HH:mm:ss' shape and ISO.
 */
export function parseUtcInstant(value) {
    if (!value)
        return null;
    const sql = DateTime.fromFormat(value, 'yyyy-MM-dd HH:mm:ss', { zone: 'utc' });
    if (sql.isValid)
        return sql;
    const iso = DateTime.fromISO(value, { zone: 'utc' });
    return iso.isValid ? iso : null;
}
/** Port of fmtInstant(). Renders a stored UTC instant in the viewer's zone. */
export function fmtInstant(utc, viewerZone, fmt = TimeFormat.INSTANT) {
    const dt = parseUtcInstant(utc);
    return dt ? dt.setZone(viewerZone).toFormat(fmt) : '';
}
/**
 * A relative timestamp: "just now", "5m ago", "3h ago", then an absolute date.
 *
 * Port of the PHP feed's feedTimeAgo(), with its thresholds unchanged. A feed is
 * read as a stream of recent things, and "7:59 PM PDT" makes the reader do the
 * subtraction; past a day the relative form stops helping and the date is more
 * useful than "37h ago".
 *
 * `nowMs` is a parameter, not `Date.now()` inside: one tick, one clock, and a
 * function that reads the wall clock itself cannot be tested.
 */
export function fmtRelative(utc, viewerZone, nowMs) {
    const dt = parseUtcInstant(utc);
    if (!dt)
        return '';
    const seconds = Math.floor((nowMs - dt.toMillis()) / 1000);
    // A clock skew between client and server can put a just-created post a second
    // or two in the future. "just now" is the honest answer, not "-1m ago".
    if (seconds < 60)
        return 'just now';
    if (seconds < 3600)
        return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86_400)
        return `${Math.floor(seconds / 3600)}h ago`;
    return dt.setZone(viewerZone).toFormat(TimeFormat.SHORT_DATE);
}
/** Format a moment for storage in an instant column. Always UTC. */
export function toUtcSql(dt) {
    return dt.toUTC().toFormat(TimeFormat.SQL_DATETIME);
}
// --- Site clock -----------------------------------------------------------
//
// The process runs in UTC and the MySQL session zone is +00:00, so a bare
// Date.now() or CURDATE() is UTC. Anything reasoning in site-local wall time
// (cron quiet hours, "is this today?", upcoming/past cuts) must come through
// here, or bind siteToday() as a query parameter instead of calling CURDATE().
/** Port of siteClock(). The current instant rendered in the site zone. */
export function siteClock(siteZone, fmt = TimeFormat.SQL_DATETIME) {
    return DateTime.now().setZone(siteZone).toFormat(fmt);
}
/** Port of siteToday(). Site-local 'yyyy-MM-dd'. The CURDATE() replacement. */
export function siteToday(siteZone) {
    return siteClock(siteZone, TimeFormat.SQL_DATE);
}
/**
 * The site-local date at a GIVEN instant, rather than now.
 *
 * Any job that both derives a date window and compares instants must take both
 * from the same clock. Mixing `siteToday()` with an injected instant is a real
 * bug, not just a testing inconvenience: a tick straddling midnight would compute
 * its candidate window from one day and its due gate from another.
 */
export function siteDateAt(siteZone, atMs) {
    return DateTime.fromMillis(atMs).setZone(siteZone).toFormat(TimeFormat.SQL_DATE);
}
/** Port of siteHour(). Site-local hour 0-23. Cron quiet hours only. */
export function siteHour(siteZone) {
    return DateTime.now().setZone(siteZone).hour;
}
/**
 * The site-local hour at a GIVEN instant.
 *
 * Same rule as siteDateAt(): a job that derives windows from an injected clock
 * must take its hour from that clock too. The cron dispatcher gates on quiet
 * hours AND on per-event instants, and reading the hour from the real clock while
 * gating events on an injected one makes the quiet-hours branch untestable — and
 * wrong for any run that is not "now".
 */
export function siteHourAt(siteZone, atMs) {
    return DateTime.fromMillis(atMs).setZone(siteZone).hour;
}
/** Site-local 'HH:mm:ss'. The CURTIME() replacement. */
export function siteTimeOfDay(siteZone) {
    return siteClock(siteZone, 'HH:mm:ss');
}
/**
 * Add or subtract whole days from a 'yyyy-MM-dd' string without any zone
 * involvement. Used to widen cron candidate windows (§6.4).
 */
export function shiftDate(date, days) {
    const dt = DateTime.fromFormat(date, 'yyyy-MM-dd', { zone: 'utc' });
    if (!dt.isValid)
        throw new Error(`Invalid date: ${date}`);
    return dt.plus({ days }).toFormat(TimeFormat.SQL_DATE);
}
export { DateTime };
//# sourceMappingURL=time.js.map